import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import type {
  QuestionField,
  ReviewItem
} from './shareItemMapping';
import {
  ItemLevelShareOptions,
  QuestionCopyTable
} from './shareItemSections';

interface ItemShareOptionsProps {
  includeRoom: boolean;
  includePhotos: boolean;
  includeFloor: boolean;
  onToggleRoom: () => void;
  onTogglePhotos: () => void;
  onToggleFloor: () => void;
}

type MetadataOnlyShareSectionProps = ItemShareOptionsProps;

interface NewItemQuestionCopySectionProps extends ItemShareOptionsProps {
  targetItem: ReviewItem;
  sourceFields: QuestionField[];
  questionsToCopy: Set<string>;
  onToggleCopy: (sourceQuestionId: string) => void;
}

export const MetadataOnlyShareSection: React.FC<MetadataOnlyShareSectionProps> = ({
  includeRoom,
  includePhotos,
  includeFloor,
  onToggleRoom,
  onTogglePhotos,
  onToggleFloor
}) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>4. Shared Data</Text>
    <Text style={styles.subtitle}>
      This item has no questions. Room, floor, and photos can still be shared:
    </Text>
    <ItemLevelShareOptions
      includeRoom={includeRoom}
      includePhotos={includePhotos}
      includeFloor={includeFloor}
      onToggleRoom={onToggleRoom}
      onTogglePhotos={onTogglePhotos}
      onToggleFloor={onToggleFloor}
    />
  </View>
);

export const NewItemQuestionCopySection: React.FC<NewItemQuestionCopySectionProps> = ({
  targetItem,
  sourceFields,
  questionsToCopy,
  includeRoom,
  includePhotos,
  includeFloor,
  onToggleRoom,
  onTogglePhotos,
  onToggleFloor,
  onToggleCopy
}) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>4. Copy Questions</Text>
    <Text style={styles.subtitle}>
      Select which questions to add to "{targetItem.name}":
    </Text>

    <ItemLevelShareOptions
      includeRoom={includeRoom}
      includePhotos={includePhotos}
      includeFloor={includeFloor}
      onToggleRoom={onToggleRoom}
      onTogglePhotos={onTogglePhotos}
      onToggleFloor={onToggleFloor}
    />

    <QuestionCopyTable
      fields={sourceFields}
      questionsToCopy={questionsToCopy}
      onToggleCopy={onToggleCopy}
    />

    <View style={styles.mappingSummary}>
      <Text style={styles.mappingCount}>
        {questionsToCopy.size} of {sourceFields.length} questions selected
      </Text>
      <Text style={styles.hint}>
        These questions will be copied to the target item and automatically shared.
      </Text>
    </View>
  </View>
);

const styles = StyleSheet.create({
  section: {
    marginBottom: 20
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16
  },
  mappingSummary: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#E3F2FD',
    borderRadius: 4
  },
  mappingCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1976D2',
    marginBottom: 4
  },
  hint: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic'
  }
});
